import React, { useMemo, useState } from "react";
import {
  Box,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { normalizeSearch, resolvePlayer } from "./displayHelpers";
import { poolsHaveResults } from "./navigation";
import PoolDetailView from "./PoolDetailView";
import PoolSummaryCard from "./PoolSummaryCard";
import type { PlayerDirectory, Pool } from "./types";
import styles from "./Resultats.module.css";

interface PoolsListViewProps {
  pools: Pool[];
  players: PlayerDirectory;
  selectedPoolId: string | null;
  highlightedLicence?: string | null;
  onSelectLicence?: (licence: string) => void;
  onSelectPool: (poolId: string | null) => void;
}

export default function PoolsListView({
  pools,
  players,
  selectedPoolId,
  highlightedLicence = null,
  onSelectLicence,
  onSelectPool,
}: PoolsListViewProps): React.ReactElement {
  const [filter, setFilter] = useState("");
  const showResults = poolsHaveResults(pools);

  const selectedPool = useMemo(
    () => pools.find((p) => p.id === selectedPoolId) ?? null,
    [pools, selectedPoolId]
  );

  const filtered = useMemo(() => {
    const q = normalizeSearch(filter);
    if (!q) return pools;
    return pools.filter((pool) => {
      if (normalizeSearch(pool.label).includes(q)) return true;
      if (normalizeSearch(pool.code).includes(q)) return true;
      return pool.standings.some((standing) => {
        if (!standing.licence) return false;
        const identity = resolvePlayer(standing.licence, players);
        return (
          normalizeSearch(identity.displayName).includes(q) ||
          normalizeSearch(identity.club ?? "").includes(q) ||
          standing.licence.includes(q)
        );
      });
    });
  }, [pools, players, filter]);

  if (selectedPool) {
    return (
      <PoolDetailView
        pool={selectedPool}
        pools={pools}
        players={players}
        highlightedLicence={highlightedLicence}
        onSelectLicence={onSelectLicence}
        onBack={() => onSelectPool(null)}
        onSelectPool={(id) => onSelectPool(id)}
      />
    );
  }

  return (
    <Box>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 1.75, lineHeight: 1.55, maxWidth: 40 * 16 }}
      >
        {showResults
          ? "Classements mis à jour au fil des scores. Ouvrez une poule pour voir les rencontres."
          : "Tirage publié : la composition de chaque poule est visible ci-dessous. Les classements apparaîtront dès les premiers résultats."}
      </Typography>

      <TextField
        fullWidth
        size="small"
        label="Filtrer une poule ou un joueur"
        value={filter}
        onChange={(event) => setFilter(event.target.value)}
        sx={{
          mb: 2.25,
          maxWidth: 440,
          "& .MuiOutlinedInput-root": { borderRadius: 2.5 },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
        }}
      />

      {filtered.length === 0 ? (
        <Typography color="text.secondary">
          Aucune poule ne correspond à « {filter.trim()} ».
        </Typography>
      ) : (
        <Box className={styles.poolsSummaryGrid}>
          {filtered.map((pool) => (
            <PoolSummaryCard
              key={pool.id}
              pool={pool}
              players={players}
              highlightedLicence={highlightedLicence}
              showResults={showResults}
              onSelect={() => onSelectPool(pool.id)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
